import { useState, useEffect } from 'react';
import api from '../services/api';
import useCurrentUser from '../hooks/useCurrentUser';
import patchNotes from '../data/patchNotes';


const ACCENT_COLORS = [
    // Rouges
    { label: 'Rouge', value: '#dc2626' },
    { label: 'Brique', value: '#b91c1c' },
    { label: 'Bordeaux', value: '#9f1239' },
    // Roses
    { label: 'Rose', value: '#db2777' },
    { label: 'Fuchsia', value: '#a21caf' },
    // Oranges
    { label: 'Orange', value: '#ea580c' },
    { label: 'Ambre', value: '#d97706' },
    { label: 'Jaune', value: '#ca8a04' },
    { label: 'Marron', value: '#92400e' },
    // Verts
    { label: 'Vert lime', value: '#65a30d' },
    { label: 'Vert', value: '#16a34a' },
    { label: 'Émeraude', value: '#059669' },
    { label: 'Sarcelle', value: '#0f766e' },
    { label: 'Turquoise', value: '#0d9488' },
    // Bleus
    { label: 'Cyan', value: '#0891b2' },
    { label: 'Bleu', value: '#2563eb' },
    { label: 'Bleu nuit', value: '#1e40af' },
    // Violets
    { label: 'Indigo', value: '#4338ca' },
    { label: 'Violet', value: '#4f46e5' },
    { label: 'Violet foncé', value: '#7c3aed' },
    { label: 'Prune', value: '#6b21a8' },
    // Gris
    { label: 'Gris ardoise', value: '#475569' },
    { label: 'Gris bleuté', value: '#334155' },
    { label: 'Noir', value: '#1a1a2e' },
];

export default function SettingsPage({ preferences, savePreferences }) {
    const [users, setUsers] = useState([]);
    const [saved, setSaved] = useState(false);
    const currentUserInfo = useCurrentUser();
    const [currentUserIri, setCurrentUserIri] = useState(null);
    const [passwordForm, setPasswordForm] = useState({ old: '', new: '', confirm: '' });
    const [passwordError, setPasswordError] = useState('');
    const [passwordSaved, setPasswordSaved] = useState(false);

    const handleSave = async (newPrefs) => {
        await savePreferences(newPrefs);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
    };

    useEffect(() => {
        api.get('/users').then(res => {
            const allUsers = res.data.member || [];
            setUsers(allUsers);
            const me = allUsers.find(u => u.email === currentUserInfo?.email);
            if (me) setCurrentUserIri(me['@id']);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordError('');
        if (passwordForm.new !== passwordForm.confirm) {
            setPasswordError('Les nouveaux mots de passe ne correspondent pas.');
            return;
        }
        if (passwordForm.new.length < 6) {
            setPasswordError('Le mot de passe doit faire au moins 6 caractères.');
            return;
        }
        try {
            const me = users.find(u => u.email === currentUserInfo?.email);
            if (!me) return;
            const id = me['@id']?.split('/').pop();
            await api.post(`/users/${id}/change-password`, {
                oldPassword: passwordForm.old,
                newPassword: passwordForm.new,
            });
            setPasswordSaved(true);
            setPasswordForm({ old: '', new: '', confirm: '' });
            setTimeout(() => setPasswordSaved(false), 3000);
        } catch (err) {
            setPasswordError(err.response?.data?.error || 'Ancien mot de passe incorrect.');
        }
    };

    return (
        <div style={{ padding: '24px', maxWidth: '600px' }}>
            <h2 style={{ margin: '0 0 24px', fontSize: '22px', fontWeight: '700', color: '#1a1a2e' }}>
                Paramètres
            </h2>

            {saved && (
                <div style={{ backgroundColor: '#dcfce7', color: '#16a34a', padding: '10px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px', fontWeight: '600' }}>
                    ✅ Préférences sauvegardées !
                </div>
            )}

            {/* Couleur d'accent */}
            <div style={sectionStyle}>
                <h3 style={sectionTitleStyle}>🎨 Couleur d'accent</h3>
                <p style={sectionDescStyle}>Couleur principale de l'interface</p>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {ACCENT_COLORS.map(color => (
                        <div
                            key={color.value}
                            onClick={() => handleSave({ accentColor: color.value })}
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: color.value,
                                cursor: 'pointer',
                                border: preferences.accentColor === color.value ? '3px solid #1a1a2e' : '3px solid transparent',
                                boxShadow: preferences.accentColor === color.value ? '0 0 0 2px white inset' : 'none',
                                transition: 'transform 0.1s',
                            }}
                            title={color.label}
                        />
                    ))}
                </div>
            </div>

            {/* Thème */}
            <div style={sectionStyle}>
                <h3 style={sectionTitleStyle}>🌙 Thème</h3>
                <p style={sectionDescStyle}>Choisir entre thème clair et sombre</p>
                <div style={{ display: 'flex', gap: '12px' }}>
                    {[
                        { label: '☀️ Clair', value: false },
                        { label: '🌙 Sombre', value: true },
                    ].map(theme => (
                        <button
                            key={String(theme.value)}
                            onClick={() => handleSave({ darkMode: theme.value })}
                            style={{
                                padding: '10px 20px',
                                borderRadius: '8px',
                                border: '2px solid',
                                borderColor: preferences.darkMode === theme.value ? '#4f46e5' : '#e0e0e0',
                                backgroundColor: preferences.darkMode === theme.value ? '#ede9fe' : 'white',
                                color: preferences.darkMode === theme.value ? '#4f46e5' : '#666',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '600',
                            }}
                        >
                            {theme.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Couleur de mes cartes */}
            <div style={sectionStyle}>
                <h3 style={sectionTitleStyle}>🎨 Ma couleur sur les cartes</h3>
                <p style={sectionDescStyle}>Les autres verront tes livraisons avec cette couleur</p>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {ACCENT_COLORS.map(color => (
                        <div
                            key={color.value}
                            onClick={() => {
                                const currentUser = users.find(u => u.email === currentUserInfo?.email);
                                if (!currentUser) return;
                                handleSave({
                                    livreurColors: {
                                        ...preferences.livreurColors,
                                        [currentUser['@id']]: color.value,
                                    }
                                });
                            }}
                            style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                backgroundColor: color.value,
                                cursor: 'pointer',
                                border: preferences.livreurColors?.[currentUserIri] === color.value
                                    ? '3px solid #1a1a2e'
                                    : '2px solid transparent',
                                boxShadow: preferences.livreurColors?.[currentUserIri] === color.value
                                    ? '0 0 0 2px white inset'
                                    : 'none',
                            }}
                            title={color.label}
                        />
                    ))}
                </div>
            </div>

            {/* Changement de mot de passe */}
            <div style={sectionStyle}>
                <h3 style={sectionTitleStyle}>🔒 Changer mon mot de passe</h3>
                <p style={sectionDescStyle}>Modifie ton mot de passe de connexion</p>
                <form onSubmit={handleChangePassword}>
                    <div style={{marginBottom: '12px'}}>
                        <input
                            type="password"
                            placeholder="Ancien mot de passe"
                            value={passwordForm.old}
                            onChange={e => setPasswordForm({...passwordForm, old: e.target.value})}
                            style={inputStyle}
                            required
                        />
                    </div>
                    <div style={{marginBottom: '12px'}}>
                        <input
                            type="password"
                            placeholder="Nouveau mot de passe"
                            value={passwordForm.new}
                            onChange={e => setPasswordForm({...passwordForm, new: e.target.value})}
                            style={inputStyle}
                            required
                        />
                    </div>
                    <div style={{marginBottom: '12px'}}>
                        <input
                            type="password"
                            placeholder="Confirmer le nouveau mot de passe"
                            value={passwordForm.confirm}
                            onChange={e => setPasswordForm({...passwordForm, confirm: e.target.value})}
                            style={inputStyle}
                            required
                        />
                    </div>
                    {passwordError && (
                        <div style={{color: '#dc2626', fontSize: '13px', marginBottom: '12px'}}>
                            ❌ {passwordError}
                        </div>
                    )}
                    {passwordSaved && (
                        <div style={{color: '#16a34a', fontSize: '13px', marginBottom: '12px'}}>
                            ✅ Mot de passe modifié avec succès !
                        </div>
                    )}
                    <button type="submit" style={{
                        padding: '10px 20px',
                        backgroundColor: '#4f46e5',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                    }}>
                        Modifier le mot de passe
                    </button>
                </form>
            </div>
            {/* Nouveautés */}
            <div style={sectionStyle}>
                <h3 style={sectionTitleStyle}>
                    <i className="ti ti-rocket" style={{ marginRight: '8px' }} />Nouveautés
                </h3>
                <p style={sectionDescStyle}>Historique des dernières mises à jour</p>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    maxHeight: '400px',
                    overflowY: 'auto'
                }}>
                    {patchNotes.map((note, i) => (
                        <div key={i} style={{
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px',
                            padding: '12px 14px',
                            borderLeft: `3px solid ${note.status === 'done' ? '#16a34a' : '#f59e0b'}`,
                        }}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
                <span style={{
                    fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px',
                    backgroundColor: note.status === 'done' ? '#dcfce7' : '#fef3c7',
                    color: note.status === 'done' ? '#16a34a' : '#d97706',
                }}>
                    {note.status === 'done' ? '✅ Livré' : '🚧 En cours'}
                </span>
                                <span style={{fontSize: '11px', color: '#aaa'}}>
                    {new Date(note.date).toLocaleDateString('fr-FR', {day: 'numeric', month: 'long', year: 'numeric'})}
                </span>
                            </div>
                            <div style={{
                                fontSize: '14px',
                                fontWeight: '700',
                                color: '#1a1a2e',
                                marginBottom: '2px'
                            }}>{note.title}</div>
                            <div style={{fontSize: '13px', color: '#666'}}>{note.description}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const sectionStyle = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
};
const sectionTitleStyle = {margin: '0 0 4px', fontSize: '16px', fontWeight: '700', color: '#1a1a2e'};
const sectionDescStyle = {margin: '0 0 16px', fontSize: '13px', color: '#888'};
const inputStyle = {
    width: '100%',
    padding: '9px 12px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    boxSizing: 'border-box',
};